// Shared build context — resolves the document + catalog once so every
// rule reads the same channels, AV unit, amps and amp assignment instead of
// recomputing them. See README "Inputs from the document".

import type { EditorDoc, GearEntry, ListenerEntry, MediaEntry } from '@roomtreatment/core';
import { roomFootprintPolygon, type Poly2 } from '@roomtreatment/core';
import { roomFloorPolygon } from '@roomtreatment/floorplan-core';
import type { AvSpecs, Product, ProjectorSpecs } from '@roomtreatment/av-catalog';
import type { Channel, CheckBuildOptions } from './types';
import { deriveChannels } from './channels';
import { assignAmplification, type AmpGear, type AssignmentResult } from './assignment';

export interface AvUnitCtx {
  entry: GearEntry;
  product: Product;
  specs: AvSpecs;
}

export interface ProjectorCtx {
  /** The projector setup: the device, and the screen it throws at. */
  entry: MediaEntry;
  product: Product;
  specs: ProjectorSpecs;
  /** How far it throws, metres: from the projector to the plane of its
   *  screen's wall. Null while it throws at nothing (no screen yet, or a
   *  screen on a wall the room no longer has). */
  throwM: number | null;
}

export interface BuildContext {
  doc: EditorDoc;
  catalog: ReadonlyMap<string, Product>;
  opts: CheckBuildOptions;
  listener: ListenerEntry | null;
  channels: Record<string, Channel>;
  bedIds: string[];
  overheadIds: string[];
  subIds: string[];
  avUnit: AvUnitCtx | null;
  amps: AmpGear[];
  /** Only computed when an AV unit exists — README "no AV unit" → rules
   *  needing one are not applicable. */
  assignment: AssignmentResult | null;
  roomPolygon: Poly2[];
  /** The first projector setup with a projector model — PRJ-01. */
  projector: ProjectorCtx | null;
}

function resolveAvUnit(doc: EditorDoc, catalog: ReadonlyMap<string, Product>): AvUnitCtx | null {
  for (const g of doc.gear ?? []) {
    const product = catalog.get(g.productId);
    if (!product) continue; // unresolved id — generic gear, no specs
    if ((product.type === 'av_receiver' || product.type === 'av_processor') && product.specs.kind === 'av') {
      return { entry: g, product, specs: product.specs };
    }
  }
  return null;
}

function resolveAmps(doc: EditorDoc, catalog: ReadonlyMap<string, Product>): AmpGear[] {
  const amps: AmpGear[] = [];
  for (const g of doc.gear ?? []) {
    const product = catalog.get(g.productId);
    if (!product) continue;
    if (product.type === 'power_amp' && product.specs.kind === 'power_amp') {
      amps.push({ gearId: g.id, product, specs: product.specs });
    }
  }
  return amps;
}

/** A `wall:i` surface key's `i` (panel-surface's `surfaceKey`), else null. */
function wallIndex(surface: string): number | null {
  const m = /^wall:(\d+)$/.exec(surface);
  return m ? Number(m[1]) : null;
}

/** Plan distance from a projector to the line of its screen's wall: the
 *  throw, whatever wall the screen is on. A lateral offset along the wall is
 *  a lens-shift question, not part of the throw ratio, so only the distance
 *  to the wall's plane counts. */
function throwDistanceM(doc: EditorDoc, entry: MediaEntry): number | null {
  const i = entry.screen ? wallIndex(entry.screen.surface) : null;
  const poly = roomFloorPolygon(doc);
  if (i === null || i >= poly.length) return null;
  const a = poly[i];
  const b = poly[(i + 1) % poly.length];
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len === 0) return null;
  const [x, , z] = entry.position;
  return Math.abs((b.x - a.x) * (z - a.y) - (b.y - a.y) * (x - a.x)) / len;
}

function resolveProjector(doc: EditorDoc, catalog: ReadonlyMap<string, Product>): ProjectorCtx | null {
  for (const m of doc.media ?? []) {
    if (m.kind !== 'projector' || !m.productId) continue;
    const product = catalog.get(m.productId);
    if (product?.type === 'projector' && product.specs.kind === 'projector') {
      return { entry: m, product, specs: product.specs, throwM: throwDistanceM(doc, m) };
    }
  }
  return null;
}

export function buildContext(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  opts: CheckBuildOptions,
): BuildContext {
  const { channels, bedIds, overheadIds, subIds } = deriveChannels(doc, catalog);
  const avUnit = resolveAvUnit(doc, catalog);
  const amps = resolveAmps(doc, catalog);
  const assignment = avUnit
    ? assignAmplification(doc, catalog, channels, bedIds, overheadIds, avUnit, amps)
    : null;
  const roomPolygon = roomFootprintPolygon(doc);
  const projector = resolveProjector(doc, catalog);

  return {
    doc, catalog, opts,
    listener: doc.listener,
    channels, bedIds, overheadIds, subIds,
    avUnit, amps, assignment,
    roomPolygon,
    projector,
  };
}
